import React from 'react'
import {Autocomplete, Chip, Stack, TextField, Typography, SxProps, Theme} from '@mui/material'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

interface Option {
  value: string
  label: string
}

interface MultiSelectAutocompleteNewProps {
  value: string[]
  onChange: (value: string[]) => void
  options: Option[]
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: boolean
  helperText?: string
  fullWidth?: boolean
  sx?: SxProps<Theme>
}

/**
 * Searchable input that opens a dropdown list of options; each pick lands inside the
 * input box as a removable tag (Chip with a built-in "x"), rather than a comma-joined
 * string (MultiSelectNew) or a bank of always-visible toggle chips (ChipSelectGroupNew).
 * Modeled on SelectNew's Autocomplete branch, minus the single-value constraint.
 */
export default function MultiSelectAutocompleteNew({
  value,
  onChange,
  options,
  label,
  placeholder = 'Seleziona...',
  required,
  disabled,
  error,
  helperText,
  fullWidth,
  sx,
}: MultiSelectAutocompleteNewProps): JSX.Element {
  const selectedOptions = options.filter(o => value.includes(o.value))

  return (
    <Stack spacing={0.5} sx={{width: fullWidth ? '100%' : undefined, ...sx}}>
      {label && (
        <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
          {label}{required && ' *'}
        </Typography>
      )}

      <Autocomplete
        disabled={disabled}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        multiple
        onChange={(_event, opts) => onChange(opts.map(o => o.value))}
        options={options}
        renderInput={(params) => (
          <TextField
            {...params}
            error={error}
            placeholder={selectedOptions.length ? undefined : placeholder}
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: '40px',
                borderRadius: '8px',
                backgroundColor: '#fff',
                '& fieldset': {borderColor: error ? 'error.main' : NEUTRAL_SCALE[200]},
                '&:hover fieldset': {borderColor: error ? 'error.main' : NEUTRAL_SCALE[300]},
                '&.Mui-focused fieldset': {borderColor: error ? 'error.main' : 'primary.main'},
              },
              '& .Mui-disabled': {opacity: 0.6},
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const {key, ...tagProps} = getTagProps({index})
            return (
              <Chip
                key={key}
                label={option.label}
                size="small"
                sx={{
                  borderRadius: 9999,
                  backgroundColor: NEUTRAL_SCALE[100],
                  color: NEUTRAL_SCALE[900],
                  fontWeight: 500,
                }}
                {...tagProps}
              />
            )
          })}
        size="small"
        value={selectedOptions}
      />

      {helperText && (
        <Typography variant="caption" sx={{color: error ? 'error.main' : NEUTRAL_SCALE[500]}}>
          {helperText}
        </Typography>
      )}
    </Stack>
  )
}
